import React, { useState, useEffect, useCallback } from 'react';
import { IconClose, IconSparkle } from './Icons';
import './AISettingsPanel.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

function authHeaders() {
  const token = localStorage.getItem('symphonate-token');
  return { Authorization: `Bearer ${token}` };
}

/**
 * Workspace AI settings — one tri-state control per AI teammate:
 *   Primary     — answers first on generic asks in every thread of the workspace
 *   Contributor — adds a short follow-up after the primary when it has value to add
 *   Off         — never responds and never sees the conversation
 * Exactly one enabled AI is the primary; switching the primary off promotes
 * the other AI automatically, so the one control covers both decisions.
 *
 * Changing the roster is admin+ only (server: `PATCH .../ai-settings`), but any
 * member can see how their workspace's AI teammates are configured — pass
 * `readOnly` for those callers and the controls render disabled with a note
 * instead of being hidden.
 */
export default function AISettingsPanel({ workspaceId, workspaceName, readOnly = false, onClose }) {
  const [availableAIs, setAvailableAIs] = useState([]);
  const [primaryAiId, setPrimaryAiId] = useState(null);
  const [disabledAiIds, setDisabledAiIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/ai-settings`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setError('Could not load AI settings.');
        } else {
          setAvailableAIs(data.availableAIs || []);
          setPrimaryAiId(data.primaryAiId || null);
          setDisabledAiIds(data.disabledAiIds || []);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load AI settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const enabledAIs = availableAIs.filter((ai) => !disabledAiIds.includes(ai.id));
  // The effective primary: the stored choice if it's enabled, else the first
  // enabled AI (the platform default).
  const effectivePrimaryId =
    (primaryAiId && enabledAIs.find((ai) => ai.id === primaryAiId)?.id) ||
    enabledAIs[0]?.id ||
    null;

  const save = useCallback(
    async (nextPrimary, nextDisabled) => {
      setSaving(true);
      setError(null);
      try {
        const res = await fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/ai-settings`, {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ primaryAiId: nextPrimary, disabledAiIds: nextDisabled }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not save AI settings');
        setPrimaryAiId(data.primaryAiId || null);
        setDisabledAiIds(data.disabledAiIds || []);
      } catch (e) {
        setError(e.message || 'Could not save AI settings');
      } finally {
        setSaving(false);
      }
    },
    [workspaceId],
  );

  const roleOf = (ai) => {
    if (disabledAiIds.includes(ai.id)) return 'off';
    return ai.id === effectivePrimaryId ? 'primary' : 'contributor';
  };

  const setRole = (ai, role) => {
    if (readOnly || saving || roleOf(ai) === role) return;
    const otherEnabled = availableAIs.filter(
      (a) => a.id !== ai.id && !disabledAiIds.includes(a.id),
    );
    if (role === 'primary') {
      save(
        ai.id,
        disabledAiIds.filter((id) => id !== ai.id),
      );
    } else if (role === 'contributor') {
      // Demoting the primary hands the role to the other enabled AI.
      const nextPrimary = ai.id === effectivePrimaryId ? otherEnabled[0]?.id || null : primaryAiId;
      save(
        nextPrimary,
        disabledAiIds.filter((id) => id !== ai.id),
      );
    } else {
      // Off: the remaining AI (if this was the primary) takes over as primary.
      const nextPrimary = ai.id === effectivePrimaryId ? otherEnabled[0]?.id || null : primaryAiId;
      save(nextPrimary, [...disabledAiIds.filter((id) => id !== ai.id), ai.id]);
    }
  };

  return (
    <div className="ai-settings-backdrop" onClick={onClose}>
      <div
        className="ai-settings-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="AI teammate settings"
      >
        <div className="ai-settings-header">
          <div>
            <h2>AI teammates</h2>
            <p className="ai-settings-subtitle">{workspaceName}</p>
          </div>
          <button className="ai-settings-close" onClick={onClose} aria-label="Close">
            <IconClose size={16} />
          </button>
        </div>

        {loading && <p className="ai-settings-status">Loading…</p>}
        {error && <p className="ai-settings-status ai-settings-error">{error}</p>}

        {!loading && readOnly && (
          <p className="ai-settings-status ai-settings-readonly">
            You can see how this workspace&rsquo;s AI teammates are set up. Only an admin or the
            owner can change them.
          </p>
        )}

        {!loading && (
          <div className={`ai-settings-body${readOnly ? ' read-only' : ''}`}>
            {availableAIs.map((ai) => {
              const role = roleOf(ai);
              const isOnlyEnabled = role !== 'off' && enabledAIs.length === 1;
              return (
                <div key={ai.id} className={`ai-settings-row ${role === 'off' ? 'off' : ''}`}>
                  <span
                    className="ai-settings-avatar"
                    style={{
                      color: ai.color || '#d96f43',
                      background:
                        (ai.color || '#d96f43') === '#127c6d'
                          ? 'rgba(217,111,67,0.22)'
                          : 'rgba(18,124,109,0.22)',
                    }}
                  >
                    <IconSparkle size={14} />
                  </span>
                  <span className="ai-settings-name">{ai.name}</span>
                  <div
                    className="ai-settings-segmented"
                    role="radiogroup"
                    aria-label={`${ai.name} role`}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={role === 'primary'}
                      className={`ai-settings-segment ${role === 'primary' ? 'active' : ''}`}
                      disabled={readOnly || saving}
                      onClick={() => setRole(ai, 'primary')}
                      title={`${ai.name} answers first on generic asks in every thread`}
                    >
                      Primary
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={role === 'contributor'}
                      className={`ai-settings-segment ${role === 'contributor' ? 'active' : ''}`}
                      disabled={readOnly || saving || (role === 'primary' && isOnlyEnabled)}
                      onClick={() => setRole(ai, 'contributor')}
                      title={`${ai.name} adds a short follow-up after the primary when it has something to add`}
                    >
                      Contributor
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={role === 'off'}
                      className={`ai-settings-segment ${role === 'off' ? 'active' : ''}`}
                      disabled={readOnly || saving || isOnlyEnabled}
                      onClick={() => setRole(ai, 'off')}
                      title={
                        isOnlyEnabled
                          ? 'At least one AI teammate must stay enabled'
                          : `${ai.name} never responds and never sees the conversation`
                      }
                    >
                      Off
                    </button>
                  </div>
                </div>
              );
            })}

            <p className="ai-settings-help">
              The <strong>primary</strong> AI answers first in every thread of this workspace. A{' '}
              <strong>contributor</strong> follows up after the primary only when it has something
              different to add. An AI that is <strong>off</strong> never responds and never sees the
              conversation. Tagging an AI by name (e.g. @Aimy) always gets an answer from just that
              AI{availableAIs.length > 1 ? ' — the other stays quiet' : ''}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
